/**
 * LiveAssetViewerPage — streaming-first viewer for in-flight ad packs.
 *
 * Mounted at ``/creative/live/:jobId``. Replaces the old
 * ``/ad-generation/:jobId`` deep link as the default destination after
 * launching a pack from the Brief Assets lens. The intent is to put the
 * user immediately into the *visual* live experience — full-bleed
 * skeleton-hydration grid, prominent status header, minimal chrome —
 * rather than the engineer-facing phase-strip + execution log surface.
 *
 * Once the pack reaches ``status === 'completed'``, a prominent CTA
 * lets the user jump into the canonical PackView for the finished pack.
 *
 * Includes a small dismissible debug strip showing the live tier
 * health (Firestore listener errors, pack-doc presence, publisher
 * heartbeat) so operators can validate the streaming path without
 * opening DevTools.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Check,
  ExternalLink,
  Info,
  Loader2,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';
import { LiveAdPackView } from '@/components/LiveAdPackView';
import {
  hydrationProgress,
  subscribeToLiveAdPackCombined,
  type CombinedSnapshot,
  type LiveAdDoc,
} from '@/services/liveAdPackListener';
import { adGenerationApi, getApiBase } from '@/services/api';

const AGENT_BASE_URL = getApiBase();

function packUrl(packId: string): string {
  return `/creative/packs/${encodeURIComponent(packId)}`;
}

function formatElapsed(seconds: number): string {
  if (seconds < 1) return '< 1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

export function LiveAssetViewerPage() {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const briefId = searchParams.get('brief_id');
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState<CombinedSnapshot>({
    pack: null,
    ads: {},
    adsOrdered: [],
    source: 'none',
  });
  const [debugOpen, setDebugOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = useCallback(async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await adGenerationApi.cancelJob(jobId);
    } catch (e) {
      console.warn('[LiveViewer] cancel failed', e);
    } finally {
      setCancelling(false);
    }
  }, [jobId, cancelling]);

  // Tick once a second so the elapsed timer in the header stays live.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Combined subscription — Firestore primary + HTTP polling fallback.
  // Polling is essential in dev where Firestore security rules block
  // reads (typical permission-denied error). The handler always gets a
  // single merged snapshot so the rest of the page doesn't care which
  // transport delivered it.
  useEffect(() => {
    if (!jobId) return;
    const unsub = subscribeToLiveAdPackCombined(jobId, setSnapshot);
    return () => unsub();
  }, [jobId]);

  const pack = snapshot.pack;
  const status = pack?.status ?? 'pending';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';
  const isCancelled = status === 'cancelled';
  const isTerminal = isCompleted || isFailed || isCancelled;

  const elapsedSec = useMemo(() => {
    const startIso = pack?.started_at;
    if (!startIso) return 0;
    const start = new Date(startIso).getTime();
    const end = pack?.completed_at ? new Date(pack.completed_at).getTime() : now;
    return Math.max(0, (end - start) / 1000);
  }, [pack?.started_at, pack?.completed_at, now]);

  const progressPct = Math.round(hydrationProgress(snapshot) * 100);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Top breadcrumb / back nav */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (briefId) navigate(`/creative/briefs/${briefId}`);
              else navigate(-1);
            }}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
          >
            <ArrowLeft size={14} />
            {briefId ? 'Back to brief' : 'Back'}
          </button>

          <div className="flex items-center gap-2">
            {!isTerminal && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg hover:border-rose-300 hover:text-rose-600 disabled:opacity-40 transition"
              >
                {cancelling ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Ban size={12} />
                )}
                Cancel
              </button>
            )}
            {isCompleted && pack?.pack_id && (
              <button
                onClick={() => navigate(packUrl(pack.pack_id!))}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl hover:from-emerald-600 hover:to-teal-600 transition shadow-sm"
              >
                Open finished pack <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Hero header */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-[10px] font-bold uppercase tracking-wide text-violet-700 flex items-center gap-1">
                  <Sparkles size={10} />
                  New · Live Streaming
                </div>
                <StatusPill status={status} />
              </div>
              <h1 className="text-xl font-bold text-slate-900">
                {isCompleted
                  ? 'Pack ready'
                  : isFailed
                    ? 'Pack failed'
                    : isCancelled
                      ? 'Pack cancelled'
                      : 'Generating ad pack'}
              </h1>
              <p className="text-xs text-slate-500">
                Watch each ad hydrate in real time — briefs first, then images,
                reviews, and the published asset.
              </p>
            </div>

            <div className="flex items-center gap-3 text-right">
              <Stat label="Progress" value={`${progressPct}%`} />
              <Stat
                label="Elapsed"
                value={formatElapsed(elapsedSec)}
              />
              <Stat
                label="Ads"
                value={
                  pack
                    ? `${pack.completed_ads}/${pack.expected_ads}`
                    : '—'
                }
              />
            </div>
          </div>

          {/* Phase / message strip */}
          {!isTerminal && pack?.current_phase && (
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={12} className="animate-spin text-violet-500" />
              <span className="font-mono">{pack.current_phase}</span>
              {pack.pipeline_message && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="italic">{pack.pipeline_message}</span>
                </>
              )}
            </div>
          )}

          {isFailed && pack?.error && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700">
              {pack.error}
            </div>
          )}
          {isCancelled && pack?.pipeline_message && (
            <div className="mt-4 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-600">
              {pack.pipeline_message}
            </div>
          )}
        </div>

        {/* Debug strip — collapsible, surfaces live tier health */}
        <DebugStrip
          jobId={jobId}
          snapshot={snapshot}
          open={debugOpen}
          onToggle={() => setDebugOpen((v) => !v)}
        />

        {/* Main live grid — full-bleed, not compact */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <LiveAdPackView
            jobId={jobId}
            onAdClick={(ad: LiveAdDoc) => {
              if (ad.canvas_asset_id) {
                navigate(`/canvas/asset/${ad.canvas_asset_id}`);
              } else if (ad.asset_url) {
                const url = ad.asset_url.startsWith('/')
                  ? `${AGENT_BASE_URL.replace(/\/$/, '')}${ad.asset_url}`
                  : ad.asset_url;
                window.open(url, '_blank', 'noopener');
              }
            }}
          />
        </div>

        {/* Completion CTA strip */}
        {isCompleted && pack?.pack_id && (
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
                <Check size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-900">
                  Pack ready to review
                </p>
                <p className="text-xs text-emerald-700">
                  Open the finished pack to share, edit, or push to Canvas.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(packUrl(pack.pack_id!))}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition shadow-sm"
            >
              Open pack <ExternalLink size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-right">
      <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">
        {label}
      </p>
      <p className="text-sm font-bold text-slate-700 tabular-nums">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<
    string,
    { cls: string; label: string; icon: React.ReactNode }
  > = {
    pending: {
      cls: 'bg-slate-50 text-slate-500 border-slate-200',
      label: 'Pending',
      icon: <Loader2 size={10} />,
    },
    running: {
      cls: 'bg-violet-50 text-violet-700 border-violet-200',
      label: 'Running',
      icon: <Zap size={10} />,
    },
    completed: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      label: 'Completed',
      icon: <Check size={10} />,
    },
    failed: {
      cls: 'bg-rose-50 text-rose-700 border-rose-200',
      label: 'Failed',
      icon: <X size={10} />,
    },
    cancelled: {
      cls: 'bg-slate-100 text-slate-500 border-slate-300',
      label: 'Cancelled',
      icon: <Ban size={10} />,
    },
  };
  const entry = map[status] ?? map.pending;
  return (
    <span
      className={clsx(
        'flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border',
        entry.cls,
      )}
    >
      {entry.icon}
      {entry.label}
    </span>
  );
}

function DebugStrip({
  jobId,
  snapshot,
  open,
  onToggle,
}: {
  jobId: string;
  snapshot: CombinedSnapshot;
  open: boolean;
  onToggle: () => void;
}) {
  const packPresent = !!snapshot.pack;
  const adsPresent = snapshot.adsOrdered.length;
  const fsErr = snapshot.firestoreError;
  const isPermissionDenied = fsErr?.code === 'permission-denied';

  // Health summary — single-line indicator. Polling-only is healthy too
  // (it just means we routed around blocked Firestore reads).
  let healthLabel: string;
  let healthCls: string;
  if (snapshot.source === 'firestore') {
    healthLabel = 'Live · Firestore';
    healthCls = 'text-emerald-600';
  } else if (snapshot.source === 'polling') {
    healthLabel = 'Live · Polling fallback';
    healthCls = 'text-teal-600';
  } else if (fsErr && snapshot.publisherAttached === false) {
    healthLabel = 'No publisher attached';
    healthCls = 'text-rose-600';
  } else if (fsErr) {
    healthLabel = isPermissionDenied
      ? 'Firestore blocked · awaiting fallback…'
      : 'Listener error';
    healthCls = 'text-amber-600';
  } else {
    healthLabel = 'Awaiting first write…';
    healthCls = 'text-amber-600';
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2 text-xs"
      >
        <span className="flex items-center gap-2 text-slate-500">
          <Info size={12} />
          <span className="font-mono">{jobId.slice(0, 24)}…</span>
          <span className="text-slate-300">·</span>
          <span className={clsx('font-semibold', healthCls)}>{healthLabel}</span>
        </span>
        <span className="text-slate-400">{open ? 'Hide details' : 'Show details'}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 space-y-2 text-[11px]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <DebugCell
              label="Data source"
              value={snapshot.source}
              ok={snapshot.source !== 'none'}
            />
            <DebugCell
              label="Pack doc"
              value={packPresent ? 'present' : 'absent'}
              ok={packPresent}
            />
            <DebugCell
              label="Ad subdocs"
              value={String(adsPresent)}
              ok={adsPresent > 0}
            />
            <DebugCell
              label="Status"
              value={snapshot.pack?.status ?? '—'}
              ok={!!snapshot.pack && snapshot.pack.status !== 'failed'}
            />
            <DebugCell
              label="Firestore listener"
              value={
                fsErr
                  ? `${fsErr.source}: ${fsErr.code ?? 'error'}`
                  : 'OK'
              }
              ok={!fsErr}
            />
            <DebugCell
              label="Publisher attached"
              value={
                snapshot.publisherAttached === undefined
                  ? '—'
                  : snapshot.publisherAttached
                    ? 'yes'
                    : 'no'
              }
              ok={snapshot.publisherAttached !== false}
            />
          </div>
          {fsErr && (
            <p className="text-[10px] text-slate-500 italic">
              Firestore {fsErr.source} listener: {fsErr.message}
              {isPermissionDenied && (
                <>
                  {' '}— this is the security-rules gap. The polling fallback
                  serves the publisher's local mirror so the live view still
                  works. Add a read rule on{' '}
                  <code>adpacks_live/&#123;jobId&#125;/&#123;path=**&#125;</code> to
                  switch back to push updates.
                </>
              )}
            </p>
          )}
          {!packPresent && !fsErr && snapshot.publisherAttached === false && (
            <p className="text-[10px] text-rose-600 italic">
              The agent service has the job tracked but no live publisher is
              attached. Most likely cause: the request did not include{' '}
              <code>live_view_enabled: true</code>. Check the POST payload in
              DevTools → Network.
            </p>
          )}
          {!packPresent && !fsErr && snapshot.publisherAttached === undefined && (
            <p className="text-[10px] text-slate-500 italic">
              No <code>adpacks_live/{jobId.slice(0, 12)}…</code> doc yet. Common
              causes: <code>live_view_enabled</code> wasn't sent on the request,
              the publisher couldn't bind to Firestore, or the security rules
              block reads. Polling fallback will activate within ~2s if the
              agent service has the job.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DebugCell({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold uppercase text-slate-400 tracking-wide">
        {label}
      </span>
      <span
        className={clsx(
          'font-mono font-semibold',
          ok ? 'text-emerald-700' : 'text-amber-700',
        )}
      >
        {value}
      </span>
    </div>
  );
}

export default LiveAssetViewerPage;
